import React from "react";
import { Handle, Position } from "reactflow";
import {
  Zap,
  GitBranch,
  Send,
  Clock,
  Database,
  Code,
} from "lucide-react";

const nodeShell = "relative min-w-[220px] max-w-[260px] rounded-lg border bg-card px-3 py-2.5 transition-colors";
const accent = "absolute left-0 top-0 bottom-0 w-[3px] rounded-l-lg";

function NodeBase({ icon: Icon, tone, badge, title, subtitle, showLeft = true, showRight = true, extra }) {
  const toneMap = {
    teal: "border-primary/40 hover:border-primary bg-primary/[0.04]",
    amber: "border-amber-500/40 hover:border-amber-500",
    sky: "border-sky-500/40 hover:border-sky-500",
    purple: "border-fuchsia-500/40 hover:border-fuchsia-500",
    slate: "border-border hover:border-foreground/50",
  };
  const accentMap = {
    teal: "bg-primary",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
    purple: "bg-fuchsia-500",
    slate: "bg-foreground",
  };
  return (
    <div className={`${nodeShell} ${toneMap[tone]}`}>
      <div className={`${accent} ${accentMap[tone]}`} />
      {showLeft && <Handle type="target" position={Position.Left} id="in" isConnectable />}
      <div className="flex items-start gap-2.5 pl-1.5">
        <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-caps text-[0.55rem] leading-tight">{badge}</div>
          <div className="text-sm font-semibold truncate leading-tight mt-0.5">{title}</div>
          {subtitle && <div className="text-[0.7rem] text-muted-foreground font-mono truncate mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {extra}
      {showRight && <Handle type="source" position={Position.Right} id="out" isConnectable />}
    </div>
  );
}

export const EventTriggerNode = ({ data }) => (
  <NodeBase
    icon={Zap}
    tone="teal"
    badge="TRIGGER · EVENT"
    title={data.label || "Event"}
    subtitle={data.eventType}
    showLeft={false}
  />
);

/**
 * If Condition node -- two labelled output handles:
 *   - source id "true"  (top-right)
 *   - source id "false" (bottom-right)
 * Route separately in canvas.
 */
export const IfConditionNode = ({ data }) => (
  <div className="relative min-w-[240px] max-w-[280px] rounded-lg border border-amber-500/40 hover:border-amber-500 bg-card transition-colors">
    <div className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-lg bg-amber-500" />
    <Handle type="target" position={Position.Left} id="in" isConnectable style={{ top: "50%" }} />

    {/* Two output handles, positioned near the true/false labels */}
    <Handle type="source" position={Position.Right} id="true" isConnectable style={{ top: "68%", background: "hsl(var(--primary))" }} />
    <Handle type="source" position={Position.Right} id="false" isConnectable style={{ top: "90%", background: "hsl(0 70% 50%)" }} />

    <div className="px-3 pt-2.5 pb-1.5">
      <div className="flex items-start gap-2.5 pl-1.5">
        <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center shrink-0">
          <GitBranch className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="label-caps text-[0.55rem] leading-tight text-amber-500">BRANCH · IF CONDITION</div>
          <div className="text-sm font-semibold truncate leading-tight mt-0.5">{data.label || "Condition"}</div>
          <div className="text-[0.7rem] text-muted-foreground font-mono truncate mt-0.5">{data.expression || "…"}</div>
        </div>
      </div>
    </div>

    {/* Path labels */}
    <div className="border-t border-border mt-1 divide-y divide-border">
      <div className="flex items-center justify-between px-3 py-1.5 relative">
        <span className="text-[0.65rem] font-semibold text-primary flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          IF TRUE
        </span>
        <span className="text-[0.6rem] text-muted-foreground font-mono">→ path A</span>
      </div>
      <div className="flex items-center justify-between px-3 py-1.5 relative">
        <span className="text-[0.65rem] font-semibold text-red-500 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          ELSE
        </span>
        <span className="text-[0.6rem] text-muted-foreground font-mono">→ path B</span>
      </div>
    </div>
  </div>
);

export const HttpActionNode = ({ data }) => (
  <NodeBase
    icon={Send}
    tone="sky"
    badge={`ACTION · ${data.method || "POST"}`}
    title={data.label || "HTTP Call"}
    subtitle={data.url}
  />
);

export const WaitActionNode = ({ data }) => (
  <NodeBase
    icon={Clock}
    tone="slate"
    badge="DELAY · WAIT"
    title={data.label || "Wait"}
    subtitle={`${data.value || 0} ${data.unit || "seconds"}`}
  />
);

export const SqlEnrichmentNode = ({ data }) => (
  <NodeBase
    icon={Database}
    tone="purple"
    badge="ENRICHMENT · SQL"
    title={data.label || "Data Enrichment"}
    subtitle={data.query ? data.query.replace(/\n/g, " ").slice(0, 42) + "…" : ""}
  />
);

export const CustomScriptNode = ({ data }) => (
  <NodeBase
    icon={Code}
    tone="teal"
    badge="COMPUTE · PYTHON"
    title={data.label || "Custom Script"}
    subtitle={data.code ? data.code.replace(/\n/g, " ").slice(0, 42) + "…" : ""}
  />
);

export const nodeTypes = {
  eventTrigger: EventTriggerNode,
  ifCondition: IfConditionNode,
  httpAction: HttpActionNode,
  waitAction: WaitActionNode,
  sqlEnrichment: SqlEnrichmentNode,
  customScript: CustomScriptNode,
};

export const NODE_PALETTE = [
  { type: "eventTrigger", icon: Zap, label: "Event Trigger", desc: "When an event of type X arrives" },
  { type: "ifCondition", icon: GitBranch, label: "If Condition", desc: "Branch on event attributes (TRUE / ELSE)" },
  { type: "httpAction", icon: Send, label: "HTTP Action", desc: "Call an external endpoint" },
  { type: "waitAction", icon: Clock, label: "Wait", desc: "Delay execution" },
  { type: "sqlEnrichment", icon: Database, label: "Data Enrichment", desc: "Pull data via SQL" },
  { type: "customScript", icon: Code, label: "Python Script", desc: "Custom code with variables" },
];
